import appState from '../state/AppStateContainer';

// localStorage scoped to the logged-in user, for any UI state worth remembering across reloads
// (view modes, active filters, collapsed panels, one-shot seen-flags).
//
// Every key is suffixed with the user id, so state can't leak between accounts sharing a browser
// profile. With no logged-in user there is no key at all: reads return empty and writes are
// dropped rather than landing under a shared `..._undefined` key. Callers therefore must treat
// "nothing stored" as a normal outcome, not an error.
//
// This is for preferences that belong to the device. Anything that should follow the user across
// machines belongs in `User.preferences` on the server instead (see ServiceUser.updatePreferences).
const scopedKey = (base: string): string | null => {
  const id = appState.state.token?.id;
  return id ? `${base}_${id}` : null;
};

// Storage can be blocked outright (private browsing, blocked cookies, enterprise policy) or full.
// That's a fact about the environment rather than a bug, and nothing here can fix it — so it warns
// once per session instead of on every call: both current writers sit in effects that fire on every
// month step and every filter toggle. Same once-only idea as showErrorNotification in ./index.js.
let storageWarned = false;
const noteStorageUnavailable = (op: string, err: unknown): void => {
  if (storageWarned) return;
  storageWarned = true;
  console.warn(`userScopedStorage: localStorage ${op} failed — preferences won't persist on this device`, err);
};

// Returns a bag of unvalidated values on purpose — callers validate the fields they care about,
// since anything here may be stale, hand-edited, or written by an older version of the app.
//
// Never throws. Both callers run this during render, and the app has no ErrorBoundary, so letting
// an error escape would turn one bad stored value into a blank page.
export const loadStored = (base: string): Record<string, unknown> => {
  const key = scopedKey(base);
  if (!key) return {};

  let raw: string | null;
  try {
    raw = localStorage.getItem(key);
  } catch (err) {
    noteStorageUnavailable('read', err);
    return {};
  }
  if (!raw) return {};

  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Falls through to the cleanup below.
  }

  // The value is in our own namespace and unusable, so drop it rather than re-parsing the same
  // garbage on every mount. The caller's defaults take over from here.
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage is read-only for us — the stale value stays, and the caller still gets its defaults.
  }
  return {};
};

export const saveStored = (base: string, value: unknown): void => {
  const key = scopedKey(base);
  if (!key) return;

  let json: string | undefined;
  try {
    json = JSON.stringify(value);
  } catch (err) {
    // A caller bug — a circular structure, a BigInt, or a Set/Map that should have been converted
    // first. Loud on purpose: swallowing this is exactly what makes it invisible.
    console.error(`userScopedStorage: value for "${base}" is not serialisable`, err);
    return;
  }
  // stringify returns undefined for undefined, functions and symbols. setItem would coerce that to
  // the literal string "undefined", which is truthy and poisons the key for every later read.
  if (json === undefined) {
    console.error(`userScopedStorage: refusing to store a non-JSON value for "${base}"`);
    return;
  }

  try {
    localStorage.setItem(key, json);
  } catch (err) {
    // Storage unavailable or over quota. Best-effort by design: the caller falls back to defaults
    // on the next load, which for both current callers means an unfiltered view, never a wrong one.
    noteStorageUnavailable('write', err);
  }
};

// Writes `patch` over whatever is already stored, instead of replacing it wholesale.
//
// A bundle only knows the fields it shipped with. Replacing the record means an older bundle —
// reached by a rollback, a staged deploy, or a tab that was open across a release — silently
// deletes any field a newer version added, and the newer version cannot get it back. Merging keeps
// unknown keys intact, so a version skew costs nothing more than the fields that version ignores.
export const mergeStored = (base: string, patch: Record<string, unknown>): void =>
  saveStored(base, { ...loadStored(base), ...patch });

// Migration helper for keys that were global before they were scoped per user: drops the old
// unscoped copy so it doesn't sit in every existing user's profile forever. Safe to call on every
// mount — it's a no-op once the key is gone.
export const dropUnscopedKey = (base: string): void => {
  try {
    localStorage.removeItem(base);
  } catch {
    // Deliberately silent, and correct: this only removes a legacy pre-scoping key that nothing
    // reads any more. Failing leaves a few orphan bytes in the profile and has no other effect.
  }
};
